'use client';

import { initPostHog } from '@/lib/posthog';
import { HydrationErrorBoundary } from '@ui/HydrationErrorBoundary';
import dynamic from 'next/dynamic';
import type { PostHog } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { type ReactNode, useEffect, useState } from 'react';

const PostHogPageView = dynamic(() => import('@/app/PostHogPageView'), {
  ssr: false,
});

const PostHogUserIdentifier = dynamic(
  () =>
    import('./PostHogUserIdentifier').then((mod) => ({
      default: mod.PostHogUserIdentifier,
    })),
  { ssr: false }
);

export function PostHogClientWrapper({ children }: { children: ReactNode }) {
  const [posthogClient, setPosthogClient] = useState<PostHog | null>(null);
  const [initError, setInitError] = useState<boolean>(false);

  useEffect(() => {
    try {
      // Initialize PostHog only on client side after hydration
      const client = initPostHog();
      setPosthogClient(client);
    } catch (error) {
      console.warn('PostHog initialization failed:', error);
      setInitError(true);
    }
  }, []);

  // During SSR, initial hydration, or if init failed
  if (!posthogClient) {
    return <>{children}</>;
  }

  return (
    <HydrationErrorBoundary>
      <PostHogProvider client={posthogClient}>
        <PostHogPageView />
        <PostHogUserIdentifier />
        {children}
      </PostHogProvider>
    </HydrationErrorBoundary>
  );
}
